// src/components/sensors/BindAssetDrawer.js
// Dedicated component for binding sensors to water infrastructure assets (Wells, Tanks, Reservoirs)

import React, { useState, useMemo } from 'react';
import {
  Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerFooter,
  Box, Text, VStack, HStack, Badge, Icon, Button, Input, InputGroup, InputLeftElement,
  Select, Divider, Flex, useColorModeValue, Center, Spinner
} from '@chakra-ui/react';
import { 
  MdOutlineWaterDrop, MdSearch, MdFilterList, 
  MdDeviceHub, MdSensors, MdErrorOutline 
} from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';

const MotionBox = motion(Box);

const BindAssetDrawer = ({ 
  isOpen, 
  onClose, 
  sensorId, 
  assets = [], 
  onBind 
}) => {
  // --- State ---
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bindingId, setBindingId] = useState(null); // Tracks which asset is currently being bound

  // --- Top-Level Theme Colors (Fixes Rules of Hooks) ---
  const headerBg = useColorModeValue('teal.50', 'teal.900');
  const borderColor = useColorModeValue('teal.100', 'teal.800');
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardHoverBg = useColorModeValue('teal.50', 'teal.900');
  const inputBg = useColorModeValue('white', 'gray.700');
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const shadowColor = useColorModeValue('sm', 'dark-lg');
  const titleColor = useColorModeValue('teal.800', 'teal.200');
  const bodyBg = useColorModeValue('gray.50', 'gray.900');
  const dividerColor = useColorModeValue('gray.200', 'gray.700');
  const emptyBorderColor = useColorModeValue('gray.300', 'gray.700');
  const btnBorderColor = useColorModeValue('gray.200', 'gray.700');
  const btnIconBg = useColorModeValue('teal.50', 'teal.900');
  const btnTextColor = useColorModeValue('gray.800', 'white');
  const footerBg = useColorModeValue('gray.50', 'gray.800');

  // --- Filtering Logic ---
  // Only show water-compatible assets
  // --- UPDATE (wells v1): drainage assets can be monitored too (flood system)
  const validTypes = ['well', 'tank', 'reservoir', 'pump', 'facility', 'drain', 'drainage', 'storm_drain', 'culvert', 'catch_basin', 'soakaway', 'outfall'];
  // --- END UPDATE

  const filteredAssets = useMemo(() => {
    // --- UPDATE (wells v1.2): "Drainage Well" / drainage_well count as wells
    const norm = (t) => { const s = String(t || '').trim().toLowerCase().replace(/[_-]+/g, ' '); return /^(drainage|storm|soak ?away|disposal|recharge) ?well$/.test(s) ? 'well' : s.replace(/ /g, '_'); };
    let filtered = assets.filter(a => validTypes.includes(norm(a.type)));

    // Apply Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(a => 
        (a.name || '').toLowerCase().includes(q) || 
        String(a.id).toLowerCase().includes(q)
      );
    }

    // Apply Type Filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(a => norm(a.type) === typeFilter);
    }

    return filtered;
  }, [assets, searchQuery, typeFilter]);

  // --- Handlers ---
  const handleBindClick = async (asset) => {
    setIsSubmitting(true);
    setBindingId(asset.id);
    try {
      await onBind(asset);
      // Wait a brief moment for the success toast/animation before closing
      setTimeout(() => {
        handleClose();
      }, 500);
    } catch (error) {
      // Error handling is managed by the parent, but we reset loading state here
      setIsSubmitting(false);
      setBindingId(null);
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setTypeFilter('all');
    setIsSubmitting(false);
    setBindingId(null);
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={handleClose} size="md">
      <DrawerOverlay backdropFilter="blur(4px)" />
      <DrawerContent borderLeftRadius="2xl" overflow="hidden">
        <DrawerCloseButton mt={2} />
        
        {/* Header */}
        <DrawerHeader borderBottomWidth="1px" borderColor={borderColor} bg={headerBg} pb={6}>
          <HStack spacing={3} mb={4}>
            <Box p={2} bg="teal.500" borderRadius="lg" color="white" shadow="md">
              <Icon as={MdDeviceHub} boxSize={6} />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontWeight="800" fontSize="xl" color={titleColor}>
                Bind to Infrastructure
              </Text>
              <Text fontSize="xs" color="teal.600" fontWeight="600">
                Map physical sensors to digital twin assets
              </Text>
            </VStack>
          </HStack>

          {/* Target Sensor Info Box */}
          <Box p={3} bg="whiteAlpha.600" borderRadius="xl" borderWidth="1px" borderColor="teal.200" backdropFilter="blur(10px)">
            <Flex justify="space-between" align="center">
              <HStack>
                <Icon as={MdSensors} color="teal.600" />
                <Text fontSize="sm" fontWeight="700" color="teal.800">Target Sensor:</Text>
              </HStack>
              <Badge colorScheme="teal" px={3} py={1} borderRadius="full" fontFamily="mono" fontSize="sm">
                {sensorId || 'Unknown ID'}
              </Badge>
            </Flex>
          </Box>
        </DrawerHeader>

        <DrawerBody py={6} px={4} bg={bodyBg}>
          <VStack spacing={4} align="stretch">
            
            {/* Search & Filters */}
            <HStack spacing={2}>
              <InputGroup size="md">
                <InputLeftElement><Icon as={MdSearch} color="gray.400" /></InputLeftElement>
                <Input 
                  placeholder="Search asset name or ID..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  bg={inputBg}
                  borderRadius="xl"
                  focusBorderColor="teal.400"
                />
              </InputGroup>
              <Select 
                size="md" 
                value={typeFilter} 
                onChange={(e) => setTypeFilter(e.target.value)}
                w="160px"
                bg={inputBg}
                borderRadius="xl"
                focusBorderColor="teal.400"
                icon={<MdFilterList />}
              >
                <option value="all">All Types</option>
                <option value="tank">Tanks</option>
                <option value="well">Wells</option>
                <option value="reservoir">Reservoirs</option>
                <option value="pump">Pumps</option>
                {/* --- UPDATE (wells v1) */}
                <option value="storm_drain">Storm drains</option>
                <option value="culvert">Culverts</option>
                <option value="drainage">Drainage</option>
                {/* --- END UPDATE */}
              </Select>
            </HStack>

            <Divider borderColor={dividerColor} />

            {/* Asset List */}
            <Box flex={1} overflowY="auto" pb={4}>
              <Text fontSize="xs" fontWeight="700" color={mutedText} textTransform="uppercase" letterSpacing="0.05em" mb={3}>
                Compatible Assets ({filteredAssets.length})
              </Text>
              
              <AnimatePresence>
                {filteredAssets.length === 0 ? (
                  <MotionBox initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <Center py={12} flexDirection="column" textAlign="center" border="2px dashed" borderColor={emptyBorderColor} borderRadius="2xl">
                      <Icon as={MdErrorOutline} boxSize={10} color="gray.400" mb={3} />
                      <Text fontWeight="600" color={mutedText}>No matching assets found</Text>
                      <Text fontSize="xs" color="gray.400" maxW="200px" mt={1}>Try adjusting your search or ensuring assets exist in the registry.</Text>
                    </Center>
                  </MotionBox>
                ) : (
                  <VStack spacing={3} align="stretch">
                    {filteredAssets.map((asset, idx) => (
                      <MotionBox 
                        key={asset.id} 
                        initial={{ opacity: 0, y: 10 }} 
                        animate={{ opacity: 1, y: 0 }} 
                        transition={{ delay: idx * 0.05 }}
                      >
                        <Button 
                          w="full" 
                          h="auto" 
                          py={4} 
                          px={5} 
                          variant="outline" 
                          justifyContent="space-between" 
                          borderRadius="xl" 
                          borderWidth="2px"
                          borderColor={bindingId === asset.id ? 'teal.400' : btnBorderColor}
                          bg={cardBg}
                          shadow={shadowColor}
                          _hover={{ borderColor: 'teal.500', bg: cardHoverBg, transform: 'translateY(-2px)', shadow: 'md' }}
                          onClick={() => handleBindClick(asset)}
                          isDisabled={isSubmitting && bindingId !== asset.id}
                          transition="all 0.2s"
                        >
                          <HStack spacing={4}>
                            <Box p={2} bg={btnIconBg} borderRadius="lg">
                              <Icon as={MdOutlineWaterDrop} color="teal.500" boxSize={5} />
                            </Box>
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="700" fontSize="sm" color={btnTextColor}>
                                {asset.name || 'Unnamed Asset'}
                              </Text>
                              <Text fontSize="xs" color={mutedText} fontFamily="mono">
                                ID: {asset.id}
                              </Text>
                            </VStack>
                          </HStack>

                          {isSubmitting && bindingId === asset.id ? (
                            <Spinner size="sm" color="teal.500" thickness="3px" />
                          ) : (
                            <Badge colorScheme="teal" px={3} py={1} borderRadius="full" fontSize="10px" fontWeight="800">
                              {asset.asset_type || asset.type}
                            </Badge>
                          )}
                        </Button>
                      </MotionBox>
                    ))}
                  </VStack>
                )}
              </AnimatePresence>
            </Box>
          </VStack>
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px" borderColor={dividerColor} bg={footerBg}>
          <Button variant="ghost" onClick={handleClose} w="full" borderRadius="xl">
            Cancel
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default BindAssetDrawer;